import { BATTLE_RES, SUP_CATS } from './constants.ts';
import { cargoUsed, shipResources, type FleetItem } from './fleet.ts';
import { fmtCount, fmtSCU } from './quantity.ts';
import { placeKey } from './rules.ts';
import type { Item, Lot, People, PrepLevel, RestockShort, Ship } from './schemas.ts';
import { amountText, crateGroups, levelStates, lowStock, materialGroups, materialText, measureText, sizesText, type LevelState } from './stockpile.ts';

// Copy-for-Discord text (plan 2B Assumption 14, R19), in Sierra's format: the
// supply report (`genSupply`, line 830) and the acquisition list (`genAcquire`,
// line 840). Times are Discord timestamps, so each reader sees their own time zone.

/** An item as the fleet rules read it. */
function toFleetItem(item: Item): FleetItem {
  return { id: item.id, name: item.name, category: item.category, unit: item.unit, battleResource: item.battleResource };
}

/** A Discord timestamp: `<t:seconds:style>`. */
export function discordTime(at: Date, style: 'f' | 'D' | 'R'): string {
  return `<t:${String(Math.floor(at.getTime() / 1000))}:${style}>`;
}

export interface ReportShip {
  id: string;
  name: string;
  hull: string;
}

export interface SupplyReportInput {
  at: Date;
  items: readonly Item[];
  lots: readonly Lot[];
  levels: readonly PrepLevel[];
  /** Active, stocked ships, in the order to list them. */
  ships: readonly ReportShip[];
  people: People;
}

/** The lines for one place's lots: per category, crates and materials grouped by item, the rest per lot. */
function supplyLines(items: readonly Item[], lots: readonly Lot[], lowAt: (lot: Lot) => LevelState | undefined, people: People, lines: string[]): void {
  const byId = new Map(items.map((item) => [item.id, item]));
  for (const category of SUP_CATS) {
    const these = lots.filter((lot) => byId.get(lot.item)?.category === category);
    if (these.length === 0) continue;
    lines.push(`__${category}__`);
    if (category === 'Crates') {
      for (const group of crateGroups(items, these)) {
        const low = group.lots.some((lot) => lowAt(lot) !== undefined);
        lines.push(`• **${group.item.name}** — ${fmtCount(group.count)} crate${group.count === 1 ? '' : 's'}, ${fmtSCU(group.scu)} SCU (${sizesText(group)})${group.locations.length > 0 ? ` @ ${group.locations.join(', ')}` : ''}${low ? ' · **LOW**' : ''}`);
      }
    } else if (category === 'Materials') {
      for (const group of materialGroups(items, these)) {
        const low = group.lots.some((lot) => lowAt(lot) !== undefined);
        const parts = group.lots.map((lot) => `${fmtSCU(lot.amount)} ${materialText(lot)}`).join(', ');
        lines.push(`• **${group.item.name}** — ${fmtSCU(group.total)} SCU: ${parts}${group.locations.length > 0 ? ` @ ${group.locations.join(', ')}` : ''}${low ? ' · **LOW**' : ''}`);
      }
    } else {
      const sorted = [...these].sort((a, b) => (byId.get(a.item)?.name ?? '').localeCompare(byId.get(b.item)?.name ?? '') || placeKey(a.place).localeCompare(placeKey(b.place)));
      for (const lot of sorted) {
        const item = byId.get(lot.item);
        if (item === undefined) continue;
        const low = lowAt(lot);
        const where = `${lot.place.kind === 'stockpile' ? ` @ ${lot.place.location}` : ''}${lot.holder === null ? '' : ` (${people[lot.holder] ?? 'a member'})`}`;
        const flag = low === undefined ? '' : ` · **LOW** (min ${measureText(item, low.level.target)})`;
        if (category === 'Kits') {
          const box = lot.boxSize === null ? '' : ` · ${String(lot.boxSize)} SCU box`;
          const contents = lot.contents.trim() === '' ? '' : `\n  ↳ ${lot.contents.replace(/\n+/g, ', ').slice(0, 160)}`;
          lines.push(`• ${item.name}${box} ×${fmtCount(lot.amount)}${where}${flag}${contents}`);
        } else {
          lines.push(`• ${item.name} — ${item.unit === 'scu' ? amountText(item, lot.amount) : `${fmtCount(lot.amount)} units`}${where}${flag}`);
        }
      }
    }
    lines.push('');
  }
}

/** Levels by item and place, for "is this lot's place low". */
function levelIndex(states: readonly LevelState[]): (lot: Lot) => LevelState | undefined {
  const low = new Map(states.filter((entry) => entry.state === 'low').map((entry) => [`${entry.level.item}|${placeKey(entry.level.place)}`, entry]));
  return (lot) => low.get(`${lot.item}|${placeKey(lot.place)}`);
}

/** The supply report (R19, `genSupply`): the stockpile, then each ship with what's aboard. */
export function supplyReport(input: SupplyReportInput): string {
  const lots = input.lots.filter((lot) => lot.amount > 0);
  if (lots.length === 0) return '## Sierra supply status\nNo supply logged yet.';
  const states = levelStates(input.items, input.lots, input.levels);
  const lowAt = levelIndex(states);
  const lines = ['## Sierra supply status', `As of ${discordTime(input.at, 'f')}`, '', '### Stockpile'];
  const stockpile = lots.filter((lot) => lot.place.kind === 'stockpile');
  if (stockpile.length === 0) lines.push('Nothing in storage.', '');
  else supplyLines(input.items, stockpile, lowAt, input.people, lines);
  let cratesAboard = false;
  const byId = new Map(input.items.map((item) => [item.id, item]));
  for (const ship of input.ships) {
    const aboard = lots.filter((lot) => lot.place.kind === 'ship' && lot.place.shipId === ship.id);
    if (aboard.length === 0) continue;
    cratesAboard ||= aboard.some((lot) => byId.get(lot.item)?.category === 'Crates');
    const shipLevels = states.filter((entry) => entry.level.place.kind === 'ship' && entry.level.place.shipId === ship.id && entry.state !== 'none');
    const short = shipLevels.filter((entry) => entry.state === 'low').length;
    const prep = shipLevels.length === 0 ? '' : short > 0 ? ` — ${String(short)} below prep level` : ' — prepped';
    lines.push(`### ${ship.name}${ship.hull !== '' && ship.hull !== ship.name ? ` (${ship.hull})` : ''}${prep}`);
    supplyLines(input.items, aboard, lowAt, input.people, lines);
  }
  if (cratesAboard) lines.push("*Crates aboard ships count as full. A partly used crate can't be measured, so ship amounts can be up to one crate's worth lower.*");
  return lines.join('\n').trim();
}

/**
 * Low stock as a post (R19): the Supply page's "Copy low stock". The full
 * acquisition list (`genAcquire`), with open requests too, is Donations' (plan 2C):
 * supply can't read donations without a dependency cycle.
 */
export function acquisitionList(input: { at: Date; items: readonly Item[]; lots: readonly Lot[]; levels: readonly PrepLevel[] }): string {
  const lines = ['## Sierra acquisition list', `As of ${discordTime(input.at, 'f')}`];
  const low = lowStock(input.items, input.lots, input.levels);
  if (low.length === 0) {
    lines.push('', 'Nothing to acquire right now.');
    return lines.join('\n');
  }
  lines.push('', '__Low stock__');
  for (const { item, level, have } of low) {
    const what = item.category === 'Crates' ? `${item.name} crates` : item.name;
    const where = level.place.kind === 'stockpile' ? ` @ ${level.place.location}` : '';
    lines.push(`• ${what} — have ${measureText(item, have)}, minimum ${measureText(item, level.target)}${where}`);
  }
  return lines.join('\n');
}

export interface ReadinessShip {
  id: string;
  name: string;
  hull: string;
  availability: Ship['availability'];
  carriedBy: string | null;
  parkedAt: string;
  cargoCapacity: number | null;
  supports: readonly string[];
  /** The owner as shown: a member's name or a guest's, or '' with none. */
  ownerName: string;
}

/**
 * The readiness report (R19, `genReadiness`, line 2707): how many stocked ships are at
 * their prep levels, then each ship with its head, "Supports:", its meta, what's
 * short and the battle resources aboard (R10).
 */
export function readinessReport(input: { at: Date; ships: readonly ReadinessShip[]; items: readonly Item[]; lots: readonly Lot[]; levels: readonly PrepLevel[]; shipNames: ReadonlyMap<string, string> }): string {
  const lines = ['## Sierra ship readiness', `As of ${discordTime(input.at, 'f')}`];
  if (input.ships.length === 0) {
    lines.push('No ships in the fleet yet.');
    return lines.join('\n');
  }
  const states = levelStates(input.items, input.lots, input.levels);
  const fleetItems = new Map(input.items.map((item) => [item.id, toFleetItem(item)]));
  const rows = input.ships.map((ship) => {
    const set = states.filter((entry) => entry.level.place.kind === 'ship' && entry.level.place.shipId === ship.id && entry.state !== 'none');
    const short = set.filter((entry) => entry.state === 'low');
    const aboard = input.lots.filter((lot) => lot.place.kind === 'ship' && lot.place.shipId === ship.id);
    return { ship, set, short, aboard, pct: set.length === 0 ? null : Math.round(((set.length - short.length) / set.length) * 100) };
  });
  const withPrep = rows.filter((row) => row.set.length > 0).length;
  const ready = rows.filter((row) => row.set.length > 0 && row.short.length === 0).length;
  lines.push(
    withPrep === 0
      ? 'No prep levels set yet. Set prep levels on what each ship carries to track readiness.'
      : `**${String(ready)} of ${String(withPrep)} ship${withPrep === 1 ? '' : 's'} at prep level**${rows.length > withPrep ? ` · ${String(rows.length - withPrep)} with no prep levels set` : ''}`,
  );
  for (const { ship, set, short, aboard, pct } of rows) {
    const head = set.length === 0 ? 'no prep levels set' : short.length > 0 ? `${String(pct)}% ready, ${String(short.length)} below prep` : 'READY';
    lines.push('', `### ${ship.name}${ship.hull !== '' && ship.hull !== ship.name ? ` (${ship.hull})` : ''} — ${head}`);
    if (ship.supports.length > 0) lines.push(`Supports: ${ship.supports.join(' · ')}`);
    const used = cargoUsed(fleetItems, aboard);
    const meta = [
      ship.availability === 'Available' ? '' : `**${ship.availability}**`,
      ship.carriedBy === null ? '' : `Carried by ${input.shipNames.get(ship.carriedBy) ?? 'another ship'}`,
      ship.parkedAt === '' ? '' : `At ${ship.parkedAt}`,
      ship.ownerName === '' ? '' : `Owner ${ship.ownerName}`,
      ship.cargoCapacity === null ? '' : `Cargo ${fmtSCU(used)} / ${fmtSCU(ship.cargoCapacity)} SCU`,
    ].filter((part) => part !== '');
    if (meta.length > 0) lines.push(meta.join(' · '));
    for (const entry of short) lines.push(`• ${entry.item.category === 'Crates' ? `${entry.item.name} crates` : entry.item.name} — ${measureText(entry.item, entry.have)} of ${measureText(entry.item, entry.level.target)}`);
    const resources = shipResources(fleetItems, aboard);
    const carried = BATTLE_RES.flatMap((resource) => {
      const found = resources[resource.id];
      return found === undefined ? [] : [`${resource.label} ${fmtSCU(found.amount)} SCU`];
    });
    if (carried.length > 0) lines.push(`Aboard: ${carried.join(' · ')}`);
  }
  const byId = new Map(input.items.map((item) => [item.id, item]));
  const shipIds = new Set(input.ships.map((ship) => ship.id));
  if (input.lots.some((lot) => lot.place.kind === 'ship' && shipIds.has(lot.place.shipId) && lot.amount > 0 && byId.get(lot.item)?.category === 'Crates')) {
    lines.push('', "*Crates aboard count as full. A partly used crate can't be measured, so amounts can be up to one crate's worth lower.*");
  }
  return lines.join('\n');
}

/** What a restock left short (R6, line 2169): `## <ship> restock: still needed`, then a line per resource. */
export function restockShortfall(shipName: string, short: readonly RestockShort[], items: readonly Item[]): string {
  const byId = new Map(items.map((item) => [item.id, item]));
  const lines = [`## ${shipName} restock: still needed`];
  for (const entry of short) {
    const item = byId.get(entry.item);
    if (item === undefined) continue;
    const need = item.unit === 'crates' || item.unit === 'scu' ? `${fmtSCU(entry.need)} SCU` : fmtCount(entry.need);
    lines.push(`• ${item.category === 'Crates' ? `${item.name} crates` : item.name} — need ${need} more`);
  }
  return lines.join('\n');
}
